import React from 'react';
import { Input, Button, Space, Table, Skeleton, InputNumber } from 'antd';
import { Formik, Form, Field } from 'formik';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import DatePicker from 'components/date-picker';
import { useI18n } from 'hooks/useI18n';
import './EditableTable.scss';

const createEmptyRow = () => ({
	id: uuidv4(),
	qty: 1,
	amount: 0,
	paid: 0,
	balance: 0,
	paidOn: moment().format('YYYY-MM-DD'),
	customerName: '',
	productName: '',
	mobile: '',
	remarks: '',
});

export const EditableTable = ({ dataSource, onSave, loading }) => {
    const { t } = useI18n();
	return (
		<Formik
			enableReinitialize
			initialValues={{
				data: dataSource.reduce((acc, row) => {
					acc[row.id] = row;
					return acc;
				}, {}),
			}}
			onSubmit={(values) => onSave(Object.values(values.data))}>
			{({ values, setFieldValue, handleSubmit }) => {
				const dataValues = Object.values(values.data);

				const initialMap = dataSource.reduce((acc, row) => {
					acc[row.id] = row;
					return acc;
				}, {});

				const isRowChanged = (row) => {
					const original = initialMap[row.id];
					if (!original) return true;
					return (
						row.customerName !== original.customerName ||
						row.productName !== original.productName ||
						row.mobile !== original.mobile ||
						row.remarks !== original.remarks ||
						row.qty !== original.qty ||
						row.amount !== original.amount ||
						row.paid !== original.paid ||
						row.balance !== original.balance ||
						row.paidOn !== original.paidOn
					);
				};

				const isRowValid = (row) => {
					return row.customerName?.trim() && row.productName?.trim() && row.qty > 0 && row.amount >= 0 && row.paid >= 0 && row.paidOn;
				};

				const handleAddRow = () => {
					const newRow = createEmptyRow();
					setFieldValue(`data.${newRow.id}`, newRow);
				};

				const handleDeleteRow = (id) => {
					const newData = { ...values.data };
					delete newData[id];
					setFieldValue('data', newData);
				};

				const totalAmount = dataValues.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
				const totalPaid = dataValues.reduce((sum, row) => sum + (Number(row.paid) || 0), 0);
				const totalBalance = dataValues.reduce((sum, row) => sum + (Number(row.balance) || 0), 0);

				const originalIds = new Set(Object.keys(initialMap));
				const currentIds = new Set(Object.keys(values.data));
				const isRowAddedOrDeleted =
					originalIds.size !== currentIds.size ||
					[...originalIds].some((id) => !currentIds.has(id)) ||
					[...currentIds].some((id) => !originalIds.has(id));

				const hasChanges = isRowAddedOrDeleted || dataValues.some((row) => isRowChanged(row));
				const allValid = dataValues.every((row) => isRowValid(row));

				const columns = [
					{
						title: t('product_expense.columns.customer'),
						dataIndex: 'customerName',
						width: 180,
						fixed: 'left',
						render: (_, record) => (
							<Field name={`data.${record.id}.customerName`}>
								{({ field }) => <Input {...field} placeholder={t('product_expense.placeholders.customerName')} style={{ width: '100%' }} />}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.mobile'),
						dataIndex: 'mobile',
						width: 140,
						fixed: 'left',
						render: (_, record) => (
							<Field name={`data.${record.id}.mobile`}>
								{({ field }) => (
									<InputNumber
										{...field}
										placeholder={t('product_expense.placeholders.mobile')}
										style={{ width: '100%' }}
										parser={(value) => value.replace(/\D/g, '')} // remove non-numeric
										formatter={(value) => value?.replace(/\D/g, '')} // show only numbers
										maxLength={10}
										onChange={(value) => {
											const numericValue = value?.toString().replace(/\D/g, '').slice(0, 10);
											setFieldValue(`data.${record.id}.mobile`, numericValue);
										}}
										value={values.data[record.id].mobile}
									/>
								)}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.product'),
						dataIndex: 'productName',
						width: 150,
						fixed: 'left',
						render: (_, record) => (
							<Field name={`data.${record.id}.productName`}>
								{({ field }) => <Input {...field} placeholder={t('product_expense.placeholders.productName')} style={{ width: '100%' }} />}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.qty'),
						dataIndex: 'qty',
						width: 100,
						render: (_, record) => (
							<Field name={`data.${record.id}.qty`}>
								{({ field }) => <Input {...field} type="number" min={1} placeholder={t('product_expense.placeholders.qty')} />}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.amount'),
						dataIndex: 'amount',
						width: 120,
						render: (_, record) => (
							<Field name={`data.${record.id}.amount`}>
								{({ field }) => (
										<Input
											{...field}
											type="number"
											placeholder={t('product_expense.placeholders.amount')}
										onChange={(e) => {
											const amount = Number(e.target.value);
											const paid = Number(values.data[record.id].paid) || 0;
											setFieldValue(`data.${record.id}.amount`, amount);
											setFieldValue(`data.${record.id}.balance`, amount - paid);
										}}
									/>
								)}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.paid'),
						dataIndex: 'paid',
						width: 120,
						render: (_, record) => (
							<Field name={`data.${record.id}.paid`}>
								{({ field }) => (
									<Input
											{...field}
											type="number"
											placeholder={t('product_expense.placeholders.paid')}
										onChange={(e) => {
											const paid = Number(e.target.value);
											const amount = Number(values.data[record.id].amount) || 0;
											setFieldValue(`data.${record.id}.paid`, paid);
											setFieldValue(`data.${record.id}.balance`, amount - paid);
										}}
									/>
								)}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.balance'),
						dataIndex: 'balance',
						width: 120,
						render: (_, record) => (
							<Field name={`data.${record.id}.balance`}>
								{({ field }) => <Input {...field} type="number" disabled placeholder={t('product_expense.placeholders.auto_balance')} />}
							</Field>
						),
					},
					{
						title: t('product_expense.columns.paid_on'),
						dataIndex: 'paidOn',
						width: 150,
						render: (_, record) => (
							<DatePicker
								style={{ width: '100%' }}
								format="DD/MM/YY"
								placeholder={t('product_expense.placeholders.date')}
								value={record.paidOn ? moment(record.paidOn) : null}
								onChange={(date) => setFieldValue(`data.${record.id}.paidOn`, date ? date.format('YYYY-MM-DD') : null)}
								allowClear={false}
							/>
						),
					},
					{
						title: t('product_expense.columns.remarks'),
						dataIndex: 'remarks',
						width: 180,
						render: (_, record) => (
							<Field name={`data.${record.id}.remarks`}>
								{({ field }) => (
									<Input.TextArea
										{...field}
										placeholder={t('product_expense.placeholders.remarks')}
										autoSize={{ minRows: 1, maxRows: 3 }}
										style={{ width: '100%' }}
									/>
								)}
							</Field>
						),
					},
					{
							title: t('product_expense.columns.actions'),
						dataIndex: 'actions',
						width: 80,
						fixed: 'right',
						render: (_, record) => (
							<Button type="text" icon={<DeleteOutlined style={{ color: 'red' }} />} onClick={() => handleDeleteRow(record.id)} />
						),
					},
				];

				const totalColumnWidth = columns.reduce((sum, col) => sum + (col.width || 100), 0);

				return (
					<Form>
						<Skeleton active loading={loading}>
							<Table
								className="custom-table"
								dataSource={dataValues}
								columns={columns}
								rowKey="id"
								pagination={false}
								scroll={{ x: totalColumnWidth }}
								rowClassName={(record) => {
									const invalid = !isRowValid(record);
									const changed = isRowChanged(record);
									if (invalid) return 'row-invalid';
									if (changed) return 'row-modified';
									return '';
								}}
								summary={() => (
									<Table.Summary.Row>
										<Table.Summary.Cell index={0} colSpan={4}>
											<strong>{t('product_expense.summary.total')}</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={4}>
											<strong>{totalAmount}</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={5}>
											<strong>{totalPaid}</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={6}>
											<strong>{totalBalance}</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={7} colSpan={4} />
									</Table.Summary.Row>
								)}
							/>
						</Skeleton>

						<Space style={{ marginTop: 16, displat: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Button disabled={!hasChanges || !allValid} onClick={handleSubmit} loading={loading}>
								{t('product_expense.actions.save')}
							</Button>
							<Button type="dashed" onClick={handleAddRow} icon={<PlusOutlined />}>
								{t('product_expense.actions.add_row')}
							</Button>
						</Space>
					</Form>
				);
			}}
		</Formik>
	);
};
